import Vue, { CreateElement } from 'src/vue'
import { Component } from 'vue-property-decorator'
import ApiClient, { ApiCallError } from 'src/lib/api_client'
import { Form } from 'src/components/form/form'
import { Input } from 'src/elements/input/input'
import { Select } from 'src/elements/select/select'
import { ClearOption, Option } from 'src/elements/select/option'
import { DatePicker } from 'src/elements/input/date-picker'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { ErrorScreen } from 'src/components/error-screen/error-screen'
import { Link } from 'src/elements/link/link'
import { List } from '@src/routes/content/components/list'
import { OptionGroup } from '@src/elements/select/option-group'
import WorkoutData, { WorkoutDataState } from '@src/data/workout-data'
import { formatDate, isMobileBrowser, track } from '@src/util/functions'
import { WorkoutService } from '@src/service/workout'
import { convertLocalDateToDate, convertToLocalDate } from '@src/util/date-format'
import EventBus from '@src/lib/event_bus'
import Member from '@src/data/member'
import { Button } from '@src/elements/button/button'
import ManageCategories from '@src/routes/content/components/categories/manage-categories'
import Category from '@src/data/category'
import { CategoryService } from '@src/service/category'
import { Icon } from '@src/elements/icon/icon'
import { Subtitle1 } from '@src/elements/typography/typography'
import { Checkbox } from '@src/elements/input/checkbox'
import SessionManager from '@src/lib/session_manager'
import { WorkoutFilters } from '@src/state/store'
import { defaultWorkoutFilters } from '@src/state/dafaults'

import './workouts.sass'
import { Modal } from '@src/elements/modal/modal'

@Component({
  computed: {
    workoutFilters () {
      return this.$store.state.workoutFilters
    },
    selectedMember () {
      return this.$store.state.selectedMember
    },
    locale () {
      return this.$store.state.i18n.locale || 'en-gb'
    }
  }
})
export class Workouts extends Vue {

  workoutFilters: WorkoutFilters
  workouts: WorkoutData[] = []
  allWorkouts: WorkoutData[] = []
  loading: boolean = true
  error: ApiCallError | null = null
  selectedMember: Member | null
  protected showManageCategories: boolean = false

  protected apiClient: ApiClient = new ApiClient()

  protected clubCategories: Category[] = []
  protected certifiedCategories: Category[] = []
  protected locale: string
  protected openedDatePicker: boolean = false

  constructor () {
    super()
    track('/content/workouts')
  }

  updated () {
    this.setDocumentTitle()
  }

  setDocumentTitle () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('MY_CONTENT')} - ${this.$root.$t('PAGE.CONTENT.WORKOUTS.TAB_NAME')}`
  }

  filterByName (value: string) {
    this.$store.dispatch('filterWorkouts', { name: value })
    return this.loadWorkouts()
  }

  filterByType (value: string) {
    this.$store.dispatch('filterWorkouts', { type: (value === 'all') ? undefined : value })
    return this.loadWorkouts()
  }

  filterFrom (value: string) {
    this.workoutFilters.from = value
    this.$store.dispatch('filterWorkouts', { created_at: this.workoutFilters.from })
    return this.loadWorkouts()
  }

  filterTo (value: string) {
    this.workoutFilters.to = value
    this.$store.dispatch('filterWorkouts', { updated_at: this.workoutFilters.to })
    return this.loadWorkouts()
  }

  filterCategories (value: number) {
    if (value === 0) {
      this.workoutFilters.categories = []
    } else {
      let index = this.workoutFilters.categories!.indexOf(value)
      if (index >= 0) {
        this.workoutFilters.categories!.splice(index, 1)
      } else {
        this.workoutFilters.categories!.push(value)
      }
    }
    this.$store.dispatch('filterWorkouts', { categories: this.workoutFilters.categories })
    return this.loadWorkouts()
  }

  filtered () {
    return this.workouts.filter((workout: WorkoutData) => {
      return this.filterByNameWorkout(workout) &&
        this.filterByTypeWorkout(workout) &&
        this.filterByFrom(workout) &&
        this.filterByTo(workout) &&
        this.filterByCategories(workout) &&
        this.filterTrashed(workout)
    })
  }
  filterByNameWorkout (workout: WorkoutData) {
    if (!this.workoutFilters.name || !this.workoutFilters.name.trim().length) {
      return true
    }
    return workout.name.toLowerCase().includes(this.workoutFilters.name.toLowerCase().trim())
  }

  filterByTypeWorkout (workout: WorkoutData) {
    // @ts-ignore
    if (!this.workoutFilters.type || this.workoutFilters.type === 'all') {
      return true
    }
    return this.workoutFilters.type === 'show' ? !workout.hidden : workout.isWorkoutByType(this.workoutFilters.type)
  }

  filterByFrom (workout: WorkoutData) {
    if (!this.workoutFilters.from) return true
    const from = convertLocalDateToDate(this.workoutFilters.from)
    return convertLocalDateToDate(convertToLocalDate(workout.createdAt)) >= from
  }

  filterByTo (workout: WorkoutData) {
    if (!this.workoutFilters.to) return true
    const to = convertLocalDateToDate(this.workoutFilters.to)
    return convertLocalDateToDate(convertToLocalDate(workout.createdAt)) <= to
  }

  filterByCategories (workout: WorkoutData) {
    if (!this.workoutFilters.categories || this.workoutFilters.categories.length === 0) return true
    return this.workoutFilters.categories.every((catId) => workout.categories.includes(catId))
  }

  filterTrashed (workout: WorkoutData) {
    return workout.state !== WorkoutDataState.TRASHED
  }

  resetFilters () {
    this.$store.dispatch('filterWorkouts', defaultWorkoutFilters())
    return this.loadWorkouts()
  }

  renderCategoryFilter (h, category: Category, certified: boolean) {
    return <div class={'category-row'} onClick={() => this.filterCategories(category.id)}>
      <div class={'name'}>
        {certified ? <Icon name='workout/certified'/> : ''}
        <Subtitle1>{category.getName(this.locale)}</Subtitle1>

      </div>

      {category.workoutsCount && category.workoutsCount}
      <Checkbox value={this.workoutFilters.categories!.includes(category.id)}
                change={() => this.filterCategories(category.id)}/>
    </div>
  }

  render (h: CreateElement) {
    let categoriesFilters = this.certifiedCategories.map((category) => this.renderCategoryFilter(h, category, true))
    categoriesFilters = categoriesFilters.concat(this.clubCategories.map((category) => this.renderCategoryFilter(h, category, false)))

    let filteredWorkouts = this.filtered()
    let typeFilterLabel = this.$t(`PAGE.CONTENT.WORKOUTS.TYPES.${{
      undefined: 'ALL',
      'home': 'HOME_WORKOUTS',
      'station': 'STATION_WORKOUTS',
      'certified': 'CERTIFIED_WORKOUTS',
      'non-certified': 'NON_CERTIFIED_WORKOUTS',
      'hidden': 'HIDDEN_WORKOUTS',
      'show': 'SHOW_WORKOUTS'
    }[`${this.workoutFilters.type}`]}`)
    return <div class='workout-wrapper'>
      <Form direction={'row'}>
        <Input search={true} ref='nameFilter' valueChanged={this.filterByName} placeholder={this.$t('SEARCH_PLACEHOLDER')}
               value={this.workoutFilters.name} />
        <Select filter={true} ref='typeFilter' value={typeFilterLabel}>
          <OptionGroup>
            <Option type='single-option' value={'all'} label={this.$t('PAGE.CONTENT.WORKOUTS.TYPES.ALL')}
                    selected={this.workoutFilters.type === undefined} select={this.filterByType}
                    detail={this.allWorkouts.length.toString()}/>
            {/*Hide home workouts*/}
            {/*<Option type='single-option' value={'home'} label={this.$t('PAGE.CONTENT.WORKOUTS.TYPES.HOME_WORKOUTS')}*/}
            {/*        selected={this.workoutFilters.type === 'station'} select={this.filterByType}*/}
            {/*        detail={t{this.workoutFilters.type === 'home'} select={this.filterByType}*/}
            {/*        detail={this.workouts.filter((w) => w.isHomeWorkout()).length.toString()} />*/}
            {/*<Option type='single-option' value={'station'} label={this.$t('PAGE.CONTENT.WORKOUTS.TYPES.STATION_WORKOUTS')}*/}
            {/*        selected=his.workouts.filter((w) => w.isStationWorkout()).length.toString()} />*/}
            <Option type='single-option' value={'certified'}
                    label={this.$t('PAGE.CONTENT.WORKOUTS.TYPES.CERTIFIED_WORKOUTS')}
                    selected={this.workoutFilters.type === 'certified'} select={this.filterByType}
                    detail={this.allWorkouts.filter((w) => w.isCertified()).length.toString()}/>
            <Option type='single-option' value={'non-certified'}
                    label={this.$t('PAGE.CONTENT.WORKOUTS.TYPES.NON_CERTIFIED_WORKOUTS')}
                    selected={this.workoutFilters.type === 'non-certified'} select={this.filterByType}
                    detail={this.allWorkouts.filter((w) => !w.isCertified()).length.toString()}/>
            <Option type='single-option' value={'hidden'}
                    label={this.$t('PAGE.CONTENT.WORKOUTS.TYPES.HIDDEN_WORKOUTS')}
                    selected={this.workoutFilters.type === 'hidden'} select={this.filterByType}
                    detail={this.allWorkouts.filter((w) => w.hidden).length.toString()}/>
            <Option type='single-option' value={'show'}
                    label={this.$t('PAGE.CONTENT.WORKOUTS.TYPES.SHOW_WORKOUTS')}
                    selected={this.workoutFilters.type === 'show'} select={this.filterByType}
                    detail={this.allWorkouts.filter((w) => !w.hidden).length.toString()}/>
          </OptionGroup>
        </Select>

        <Select filter={true} class='categories-filter' value={this.$t('PAGE.CONTENT.WORKOUTS.CATEGORIES.CATEGORIES')}>
          <OptionGroup>
            <ClearOption class='filter-clear' value='all' label={this.$t('PAGE.CONTENT.WORKOUTS.CLEAR_FILTERS')}
                         select={() => this.filterCategories(0)}/>
            {categoriesFilters}
          </OptionGroup>
        </Select>
        <Button class='manage-btn' label={this.$t('PAGE.CONTENT.WORKOUTS.CATEGORIES.MANAGE_CATEGORIES')} click={this.manageCategoriesModal}/>
        {this.showManageCategories ? <ManageCategories show={true} closeCallback={this.closeManageCategories}/> : ''}
        {this.chooseDatePickerDesktopAndMobile(h)}

      </Form>
      {this.loading ?
        <Loader/>
        :
        this.error ?
          <ErrorScreen title={this.$t('PAGE.CONTENT.WORKOUTS.REQUEST_ERROR')} message={this.$t(this.error.messageKey)}>
            <Link align='center' click={this.reloadPage} label={this.$t('TRY_AGAIN')} variant='colored'/>
          </ErrorScreen>
          :
          this.allWorkouts.length ?
            filteredWorkouts.length ?
              <List
                items={this.workouts}
                itemType='WorkoutCard'
                currentPage={this.workoutFilters.page}
                ref='workoutList'
                on={{
                  input: (newItems: any[]) => {
                    this.workouts = newItems
                  }
                }}
                changePageCallback={(newPage) => {
                  document.getElementById('component-tabs-body')!.scrollTop = 0
                  this.$store.dispatch('filterWorkouts', { page: newPage })
                }}
              />
              : <ErrorScreen
                message={this.workoutFilters.name ? this.$t('PAGE.CONTENT.WORKOUTS.NOT_FOUND.FILTER.NAME', { query: this.workoutFilters.name }) : this.$t('PAGE.CONTENT.WORKOUTS.NOT_FOUND.FILTER.GENERIC')}>
                <Link variant='colored' align='center' click={this.resetFilters}
                      label={this.$t('PAGE.CONTENT.WORKOUTS.RESET_FILTERS')}/>
              </ErrorScreen>

            : <ErrorScreen message={this.$t('PAGE.CONTENT.WORKOUTS.NO_WORKOUTS')}/>
      }
    </div>
  }
  openDateTimeModal () {
    this.openedDatePicker = true
  }

  closeDateTimeModal () {
    this.openedDatePicker = false
  }
  manageCategoriesModal () {
    this.showManageCategories = true
  }

  chooseDatePickerDesktopAndMobile (h: CreateElement) {
    return isMobileBrowser() ? (<div>
          <Icon id='time-range-icon' name='util/date-range' click={this.openDateTimeModal}/>
          <Modal show={this.openedDatePicker} closeCallback={this.closeDateTimeModal} title={this.$t('CHOOSE_TIME_PERIOD')} cssClass={'date-time-modal'}>
            {this.renderDatePicker(h)}
          </Modal>
        </div>)
    : this.renderDatePicker(h)
  }
  renderDatePicker (h: CreateElement) {
    return (<div class={'date-picker-wrapper'}>
      <DatePicker ref='created_at' label={this.$t('START_DATE')} class='creation-date'
                  value={this.workoutFilters.from} type='date' change={this.filterFrom}
                  icon='util/calendar' allowFutureDate={false}
                  firstAvailableDate={this.workoutFilters.to ? formatDate(this.workoutFilters.to,'DD/MM/YYYY','MM/DD/YYYY') : new Date() }/>
      <DatePicker ref='updated_at' label={this.$t('END_DATE')} class='edit-date' value={this.workoutFilters.to}
                  allowPastDate={!this.workoutFilters.from} allowFutureDate={!this.workoutFilters.from}
                  firstAvailableDate={this.workoutFilters.from ? formatDate(this.workoutFilters.from, 'DD/MM/YYYY', 'MM/DD/YYYY') : new Date()} type='date' change={this.filterTo} icon='util/calendar'/></div>)
  }

  async closeManageCategories () {
    await this.loadCategories()
    this.showManageCategories = false
  }

  async mounted () {
    EventBus.on('reloadWorkouts', this.loadWorkouts)
    await this.loadCategories()
    return this.loadWorkouts()
  }

  beforeDestroy () {
    EventBus.off('reloadWorkouts', this.loadWorkouts)
  }

  reloadPage () {
    this.error = null
    this.loading = true
    return Promise.all([this.loadCategories(),this.loadWorkouts()])
  }

  loadWorkouts () {
    this.loading = true
    return new WorkoutService().loadWorkouts(this.selectedMember ? this.selectedMember.id : null)
      .then((workouts: WorkoutData[]) => {
        this.allWorkouts = workouts
        if (this.workoutFilters.type === 'show') {
          this.workouts = workouts ? workouts.sort((a, b) => {
            if (a.isCertified() && b.isCertified()) {
              return a.position - b.position
            }
            if (a.isCertified()) {
              return -1
            }
            if (b.isCertified()) {
              return 1
            }
            return a.position - b.position
          }) : []
        } else {
          this.workouts = workouts ? workouts.sort((a, b) => {
            const dateA = a.platformUpdatedAt ? new Date(a.platformUpdatedAt) : new Date(a.createdAt)
            const dateB = b.platformUpdatedAt ? new Date(b.platformUpdatedAt) : new Date(b.createdAt)
            return dateB.getTime() - dateA.getTime()
          }) : []
        }
        this.workouts = this.workouts.filter((workout: WorkoutData) =>
          this.filterByNameWorkout(workout) &&
          this.filterByTypeWorkout(workout) &&
          this.filterByFrom(workout) &&
          this.filterByTo(workout) &&
          this.filterByCategories(workout) &&
          this.filterTrashed(workout)
        )
        console.log('workouts', this.workouts)
      })
      .catch((error) => this.error = error)
      .finally(() => this.loading = false)
  }

  async loadCategories () {
    if (!SessionManager.Instance.isManager()) return undefined
    let categoryService = new CategoryService()

    return Promise.all(
      [categoryService.getCategories(false, this.locale)
        .then(categories => this.clubCategories = categories)
        .catch((error) => this.error = error),
        categoryService.getCategories(true, this.locale)
          .then(categories => this.certifiedCategories = categories)
          .catch((error) => this.error = error)]
    )
  }
}
